"use client"

import { useMemo, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { Filter } from "lucide-react"
import { cn } from "@/lib/utils"
import { Skeleton } from "@/components/ui/skeleton"
import { ConditionStockTable } from "@/components/conditions/ConditionStockTable"
import { getConditionList, getConditionStocks } from "@/lib/api/conditions"
import { getStocks } from "@/lib/api/stocks"
import { getWatchlistCodes } from "@/lib/api/watchlist"
import type { EnrichedConditionStock } from "@/types/condition"

export default function ConditionsPage() {
  const [selectedSeq, setSelectedSeq] = useState<string | null>(null)

  const {
    data: conditions = [],
    isLoading: listLoading,
    isError: listError,
  } = useQuery({
    queryKey: ["conditions", "list"],
    queryFn: getConditionList,
  })

  const { data: stocks = [], isLoading: stocksLoading } = useQuery({
    queryKey: ["conditions", "stocks", selectedSeq],
    queryFn: () => getConditionStocks(selectedSeq!),
    enabled: !!selectedSeq,
  })

  const { data: allStocks = [] } = useQuery({
    queryKey: ["stocks"],
    queryFn: getStocks,
    staleTime: 5 * 60 * 1000,
  })

  const { data: watchlist = [] } = useQuery({
    queryKey: ["watchlist", "codes"],
    queryFn: getWatchlistCodes,
  })
  const watchlistCodes = useMemo(() => new Set(watchlist.map((w) => w.stockCode)), [watchlist])

  const stockMap = useMemo(
    () => new Map(allStocks.map((s) => [s.stockCode, s])),
    [allStocks]
  )

  const enrichedStocks = useMemo<EnrichedConditionStock[]>(
    () =>
      stocks.map((s) => ({
        ...s,
        market: stockMap.get(s.stockCode)?.market ?? null,
        sector: stockMap.get(s.stockCode)?.sector ?? null,
      })),
    [stocks, stockMap]
  )

  const selectedName = conditions.find((c) => c.seq === selectedSeq)?.name

  return (
    <div className="flex flex-col gap-4 md:h-full md:flex-row">
      {/* md 이상: 왼쪽 조건검색식 목록 */}
      <aside className="hidden w-48 shrink-0 flex-col gap-1 md:flex">
        <div className="mb-2 flex items-center gap-2">
          <Filter className="size-4 text-zinc-400" />
          <span className="text-sm font-semibold text-zinc-300">조건검색식</span>
        </div>

        {listLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-9 w-full rounded-md bg-zinc-800" />
          ))
        ) : listError ? (
          <p className="text-xs text-red-400">목록을 불러오지 못했습니다.</p>
        ) : conditions.length === 0 ? (
          <p className="text-xs text-zinc-500">조건검색식이 없습니다.</p>
        ) : (
          conditions.map((c) => {
            const active = selectedSeq === c.seq
            return (
              <button
                key={c.seq}
                onClick={() => setSelectedSeq(c.seq)}
                className={cn(
                  "w-full rounded-md px-3 py-2 text-left text-sm font-medium transition-colors",
                  active
                    ? "bg-emerald-500/10 text-emerald-400"
                    : "text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"
                )}
              >
                <span className="block truncate">{c.name}</span>
              </button>
            )
          })
        )}
      </aside>

      {/* md 미만: 상단 가로 스크롤 탭 */}
      <div className="flex gap-2 overflow-x-auto pb-1 md:hidden">
        {listLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-11 w-28 shrink-0 rounded-full bg-zinc-800" />
          ))
        ) : listError ? (
          <p className="text-xs text-red-400">목록을 불러오지 못했습니다.</p>
        ) : conditions.length === 0 ? (
          <p className="text-xs text-zinc-500">조건검색식이 없습니다.</p>
        ) : (
          conditions.map((c) => {
            const active = selectedSeq === c.seq
            return (
              <button
                key={c.seq}
                onClick={() => setSelectedSeq(c.seq)}
                className={cn(
                  "min-h-[44px] shrink-0 whitespace-nowrap rounded-full px-4 text-sm font-medium transition-colors",
                  active
                    ? "bg-emerald-500/10 text-emerald-400"
                    : "bg-zinc-900 text-zinc-400"
                )}
              >
                {c.name}
              </button>
            )
          })
        )}
      </div>

      {/* 오른쪽: 결과 */}
      <div className="flex min-w-0 flex-1 flex-col gap-3">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold text-zinc-100">
            {selectedName ?? "조건검색 결과"}
          </h1>
          {selectedSeq && !stocksLoading && (
            <span className="text-sm text-zinc-500">{enrichedStocks.length}개</span>
          )}
        </div>

        <div className="rounded-lg border border-zinc-800 bg-zinc-900 md:overflow-x-auto">
          {selectedSeq ? (
            <ConditionStockTable stocks={enrichedStocks} isLoading={stocksLoading} watchlistCodes={watchlistCodes} />
          ) : (
            <div className="flex h-48 items-center justify-center px-4 text-center text-zinc-500">
              <span className="hidden md:inline">왼쪽에서&nbsp;</span>
              조건검색식을 선택하세요.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
